import { useEffect, useId, useState } from "react";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "../lib/notices";
import { UserSquare2, Loader2 } from "lucide-react";

const LAYOUTS = [
  { id: "smart", name: "Smart presenter", hint: "Full-frame intro, corner bubble during the walkthrough, full-frame outro. Recommended." },
  { id: "bubble", name: "Corner bubble", hint: "Small circle over your product, the whole way through." },
  { id: "bookend", name: "Bookend", hint: "Avatar intro and outro, product-only in the middle." },
  { id: "split", name: "Split screen", hint: "Avatar beside your product throughout." },
];

const PRESENTER_PLANS = ["pro", "agency"];

export default function PresenterPanel({ projectId, presenter, onChange, language }) {
  const { user } = useAuth();
  // Stable, collision-free ids so every label/select pair here is a real
  // association rather than proximity in the DOM.
  const uid = useId();
  const avatarId = `${uid}-avatar`;
  const voiceId = `${uid}-voice`;
  const layoutLabelId = `${uid}-layout-label`;
  const [avatars, setAvatars] = useState([]);
  const [stock, setStock] = useState([]);
  const [voices, setVoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingAvatars, setLoadingAvatars] = useState(true);

  const locked = !PRESENTER_PLANS.includes((user?.plan || "free").toLowerCase());
  const cfg = presenter || { enabled: false, layout: "smart", avatar_id: null, voice_id: null };

  useEffect(() => {
    if (locked) return;
    // The catalog is large and can take seconds on a cold cache. Track loading
    // so the Enable toggle stays disabled until we actually have avatars and
    // voices to choose from — enabling without either is rejected by the backend.
    setLoadingAvatars(true);
    const voicesUrl = language ? `/avatars/voices?language=${encodeURIComponent(language)}` : "/avatars/voices";
    Promise.allSettled([
      api.get("/avatars").then((r) => setAvatars(r.data || [])),
      api.get("/avatars/stock").then((r) => setStock(r.data?.avatars || [])),
      api.get(voicesUrl).then((r) => setVoices(r.data?.voices || [])),
    ]).finally(() => setLoadingAvatars(false));
  }, [locked, language]);

  async function save(next) {
    setSaving(true);
    try {
      const r = await api.put(`/projects/${projectId}/presenter`, next);
      onChange?.(r.data.presenter);
    } catch (e) {
      toast.error(errMsg(e, "Could not save presenter settings."));
    } finally {
      setSaving(false);
    }
  }

  if (locked) {
    return (
      <div className="brutal-card p-4">
        <div className="flex items-center gap-2 font-bold uppercase tracking-widest text-sm">
          <UserSquare2 size={16} /> AI avatar presenter
        </div>
        <p className="text-sm text-ink-muted mt-2">
          Put a presenter in front of your product demo. Available on Pro and Agency.
        </p>
        <a href="/pricing" className="brutal-btn inline-block mt-3 text-xs">Upgrade to unlock →</a>
      </div>
    );
  }

  const readyAvatars = [
    ...avatars.filter((a) => a.status === "ready").map((a) => ({ id: a.provider_avatar_id, name: `${a.name} (yours)` })),
    ...stock,
  ];

  return (
    <div className="brutal-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold uppercase tracking-widest text-sm">
          <UserSquare2 size={16} /> AI avatar presenter
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={loadingAvatars}
            checked={!!cfg.enabled}
            onChange={(e) => {
              if (!e.target.checked) return save({ ...cfg, enabled: false });
              // The avatar/voice pickers only render once enabled, but the backend
              // silently skips the presenter when either avatar_id or voice_id is
              // missing (a config with an avatar but no voice renders no presenter
              // at all). Seed both defaults on the way in, otherwise the pickers
              // can never appear. The user can change them immediately after.
              const seedAvatar = cfg.avatar_id || readyAvatars[0]?.id;
              const seedVoice = cfg.voice_id || voices[0]?.id;
              if (!seedAvatar) {
                toast.error("No avatars available yet — check the HeyGen configuration.");
                return;
              }
              if (!seedVoice) {
                toast.error("No voices available yet — check the HeyGen configuration.");
                return;
              }
              save({ ...cfg, enabled: true, avatar_id: seedAvatar, voice_id: seedVoice });
            }}
          />
          {loadingAvatars ? (
            <span className="flex items-center gap-1 text-ink-muted">
              <Loader2 size={12} className="animate-spin" /> Loading avatars…
            </span>
          ) : (
            "Enable"
          )}
        </label>
      </div>

      {cfg.enabled && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor={avatarId}>Presenter</label>
            <select
              id={avatarId}
              className="brutal-select w-full mt-1"
              value={cfg.avatar_id || ""}
              onChange={(e) => save({ ...cfg, avatar_id: e.target.value })}
            >
              <option value="">Choose an avatar…</option>
              {readyAvatars.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-ink-muted" htmlFor={voiceId}>Voice</label>
            <select
              id={voiceId}
              className="brutal-select w-full mt-1"
              value={cfg.voice_id || ""}
              onChange={(e) => save({ ...cfg, voice_id: e.target.value })}
            >
              <option value="">Choose a voice…</option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>{v.name}{v.language ? ` (${v.language})` : ""}</option>
              ))}
            </select>
          </div>

          <div>
            {/* Not a <label>: this labels a group of toggle buttons, and a
                <label> may only point at a form control. */}
            <div id={layoutLabelId} className="text-xs uppercase tracking-widest text-ink-muted">Layout</div>
            <div className="grid grid-cols-2 gap-2 mt-1" role="group" aria-labelledby={layoutLabelId}>
              {LAYOUTS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  aria-pressed={cfg.layout === l.id}
                  onClick={() => save({ ...cfg, layout: l.id })}
                  className={`brutal-border text-left p-2 text-sm ${cfg.layout === l.id ? "bg-brand text-ink-on-accent" : "bg-white"}`}
                >
                  <div className="font-bold">{l.name}</div>
                  <div className="text-xs">{l.hint}</div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {saving && (
        <p className="text-xs text-ink-muted flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Saving…
        </p>
      )}
    </div>
  );
}
